import { get } from "svelte/store"
import {
  adminResources,
  createContextMutationPrecondition,
  parseContextSession,
  type ChangeMembershipAccessRequest,
  type ContextSessionResponse,
  type ControlWebClient,
  type OrganizationRoleChangeResponse,
  type OrganizationRoleResponse,
  type ReplaceOrganizationRoleRequest,
  type RequestOptions,
} from "@kontextwerk/control-web-client"
import {
  captureControlContext,
  controlContext,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { getControlWebClient } from "../client"
import { createMembershipInvitationsApi } from "../membership-invitations"
import {
  invalid,
  membershipChange,
  membershipPage,
  relationshipStatus,
  revision,
  uuid,
  type KehrblattMembership,
  type KehrblattMembershipChange,
  type KehrblattMembershipPage,
} from "./_logic/contracts"

export type {
  KehrblattMembership,
  KehrblattMembershipChange,
  KehrblattMembershipPage,
} from "./_logic/contracts"

type Capture = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>

const MAX_MEMBERSHIP_PAGES = 50

/**
 * Memberships and roles of the confirmed KB organization through the KB product transport.
 * Memberships are parsed locally because the server may add the display-only `email`; roles keep
 * the published client contract. Every request carries the captured context pair; writes are
 * never replayed and a stale context never reads or writes.
 */
export const createIdentityAdministrationApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Capture,
) => {
  const session = parseContextSession(value)
  const pair = createContextMutationPrecondition(capture.mutationContext)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== pair.expected_session_id ||
    session.context_revision !== pair.expected_context_revision
  )
    throw new TypeError("Unconfirmed identity administration context")

  const organizationId = uuid(session.context.organization_id)
  const path = `/admin/organizations/${organizationId}`
  const roles = new adminResources.AdminIdentityAdministrationAPI(client.transport)
  const bound = (signal?: AbortSignal): Capture => ({
    signal: signal ? AbortSignal.any([capture.signal, signal]) : capture.signal,
    isCurrent: capture.isCurrent,
    mutationContext: pair,
  })
  const memberships = async (
    after: string | null,
    signal?: AbortSignal,
  ): Promise<KehrblattMembershipPage> =>
    membershipPage(
      await client.transport.request<unknown>("GET", `${path}/memberships`, {
        ...bound(signal),
        retry: "never",
        params: { limit: 100, ...(after ? { after_id: uuid(after) } : {}) },
      }),
      organizationId,
    )

  return {
    organizationId,
    memberships,
    /** Complete roster; a cursor that repeats or does not advance is a contract failure. */
    async allMemberships(signal?: AbortSignal): Promise<readonly KehrblattMembership[]> {
      const items: KehrblattMembership[] = []
      const cursors = new Set<string>()
      let after: string | null = null
      for (let page = 0; page < MAX_MEMBERSHIP_PAGES; page += 1) {
        const result: KehrblattMembershipPage = await memberships(after, signal)
        items.push(...result.items)
        after = result.next_after_id

        if (!after) {
          if (new Set(items.map((item) => item.id)).size !== items.length) return invalid()

          return Object.freeze(items)
        }

        if (cursors.has(after) || !result.items.length) return invalid()

        cursors.add(after)
      }

      return invalid()
    },
    async changeMembership(
      id: string,
      payload: ChangeMembershipAccessRequest,
    ): Promise<KehrblattMembershipChange> {
      const key = uuid(id)

      return membershipChange(
        await client.transport.request<unknown>("PATCH", `${path}/memberships/${key}`, {
          ...bound(),
          retry: "never",
          requireCsrf: true,
          json: {
            role_id: uuid(payload.role_id),
            status: relationshipStatus(payload.status),
            expected_revision: revision(payload.expected_revision),
          },
        }),
        organizationId,
        key,
      )
    },
    async roles(signal?: AbortSignal): Promise<readonly OrganizationRoleResponse[]> {
      return (await roles.roles(organizationId, bound(signal))).items
    },
    role(id: string, signal?: AbortSignal): Promise<OrganizationRoleResponse> {
      return roles.role(organizationId, id, bound(signal))
    },
    replaceRole(
      id: string,
      payload: ReplaceOrganizationRoleRequest,
    ): Promise<OrganizationRoleChangeResponse> {
      return roles.replaceRole(organizationId, id, payload, bound())
    },
  }
}
export type IdentityAdministrationApi = ReturnType<typeof createIdentityAdministrationApi>

/**
 * Captures the confirmed organization context once for one administration owner. Returns null
 * while no matching KB organization context is confirmed; nothing is read or written then.
 */
export const captureIdentityAdministration = (organizationId: string) => {
  const context = get(controlContext)
  const session = context?.session ?? null

  if (
    !session ||
    session.product_issuer !== "kehrblatt-control" ||
    session.password_change_required ||
    session.context.kind !== "organization" ||
    context?.organizationId?.toLowerCase() !== organizationId.toLowerCase()
  )
    return null

  const capture = captureControlContext()

  if (!capture || capture.signal.aborted || !capture.isCurrent()) return null

  const client = getControlWebClient()
  let access: IdentityAdministrationApi
  let invitations: ReturnType<typeof createMembershipInvitationsApi>
  try {
    access = createIdentityAdministrationApi(client, session, capture)
    invitations = createMembershipInvitationsApi(client, session, capture)
  } catch {
    // The published session and the request binding belong to different context pairs.
    return null
  }

  return {
    session,
    signal: capture.signal,
    current: () => !capture.signal.aborted && capture.isCurrent(),
    access,
    invitations,
  }
}
export type CapturedIdentityAdministration = NonNullable<
  ReturnType<typeof captureIdentityAdministration>
>
